import { useCallback, useMemo, useState } from 'react';
import { useColorAssignment } from '@/hooks';
import { checkboxColors } from '@/utils';
import {
  INITIAL_LIST_STATUS,
  SelectableMetricsContext,
  type ListQueryStatus,
} from './SelectableMetricsContext';
import type { SelectedItem } from '@/types/content';

type SelectionState = {
  items: SelectedItem[];
  /**
   * True after the user changes selection (including clearing it). False on first load and after
   * reset. Distinguishes "never picked" (→ auto-seed) from "cleared on purpose" (→ leave empty).
   */
  hasTouched: boolean;
};

const INITIAL_SELECTION: SelectionState = { items: [], hasTouched: false };

/**
 * Owns the selection shared between the metrics table and chart: the table drives which
 * items are selected, the chart renders a line per selected item in its assigned colour.
 * Colour is resolved to a CSS string once here (via `useColorAssignment` + `checkboxColors`)
 * so consumers read `item.color` instead of repeating the index→colour lookup.
 */
export const SelectableMetricsView = ({ children }: { children: React.ReactNode }) => {
  const [selection, setSelection] = useState<SelectionState>(INITIAL_SELECTION);
  const [listStatus, setListStatus] = useState<ListQueryStatus>(INITIAL_LIST_STATUS);

  // TODO: Consider inlining once this component is rolled out.
  const { getColor, reset: resetColors } = useColorAssignment(
    selection.items.map((item) => item.id)
  );

  const selectedItems = useMemo(
    () =>
      selection.items.map((item) => ({
        ...item,
        color: checkboxColors[getColor(item.id) ?? 0],
      })),
    [selection.items, getColor]
  );

  const select = useCallback((items: SelectedItem[]) => {
    setSelection({ items, hasTouched: true });
  }, []);

  const seedSelection = useCallback((items: SelectedItem[]) => {
    if (items.length === 0) return;
    setSelection((current) => {
      if (current.hasTouched || current.items.length > 0) return current;
      return { ...current, items };
    });
  }, []);

  const updateListStatus = useCallback((status: ListQueryStatus) => {
    setListStatus((current) =>
      current.isListPending === status.isListPending && current.isListEmpty === status.isListEmpty
        ? current
        : status
    );
  }, []);

  const reset = useCallback(() => {
    resetColors();
    setSelection(INITIAL_SELECTION);
    setListStatus(INITIAL_LIST_STATUS);
  }, [resetColors]);

  const value = useMemo(
    () => ({
      selectedItems,
      select,
      seedSelection,
      reset,
      hasTouched: selection.hasTouched,
      isListPending: listStatus.isListPending,
      isListEmpty: listStatus.isListEmpty,
      updateListStatus,
    }),
    [
      selectedItems,
      select,
      seedSelection,
      reset,
      selection.hasTouched,
      listStatus,
      updateListStatus,
    ]
  );

  return (
    <SelectableMetricsContext.Provider value={value}>{children}</SelectableMetricsContext.Provider>
  );
};
